import { useState } from "react";
import { useMutation } from "@apollo/client";
import {
  CREATE_FEATURE_BANNER,
  UPDATE_FEATURE_BANNER
} from "../../../graphql/feature-banners";
import adminClient from "../../../graphql/admin-client";
import { useToast } from "@teamrota/rota-design";

export const useBannerForm = (banner, onSuccess) => {
  const isEdit = !!banner;
  const { showToast } = useToast();

  const [formData, setFormData] = useState({
    status: banner?.status || "DRAFT",
    audience: banner?.audience || "ALL",
    title: banner?.title || "",
    message: banner?.message || "",
    ctaLabel: banner?.ctaLabel || "Open",
    deeplink: banner?.deeplink || "",
    externalUrl: banner?.externalUrl || "",
    minAppVersion: banner?.minAppVersion || "",
    maxAppVersion: banner?.maxAppVersion || "",
    platforms: banner?.platforms || ["ios", "android"],
    startsAt: banner?.startsAt
      ? new Date(banner.startsAt).toISOString().slice(0, 16)
      : "",
    endsAt: banner?.endsAt
      ? new Date(banner.endsAt).toISOString().slice(0, 16)
      : "",
    dismissible: banner?.dismissible ?? true,
    variant: banner?.variant || "INFO",
    icon: banner?.icon || ""
  });

  const [
    createBanner,
    { loading: creating }
  ] = useMutation(CREATE_FEATURE_BANNER, { client: adminClient });

  const [
    updateBanner,
    { loading: updating }
  ] = useMutation(UPDATE_FEATURE_BANNER, { client: adminClient });

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handlePlatformChange = platform => {
    setFormData(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter(p => p !== platform)
        : [...prev.platforms, platform]
    }));
  };

  const validateForm = () => {
    if (!formData.message) {
      showToast("Message is required", { severity: "error" });
      return false;
    }

    if (formData.deeplink && formData.externalUrl) {
      showToast("Cannot provide both deeplink and external URL", {
        severity: "error"
      });
      return false;
    }

    if (formData.platforms.length === 0) {
      showToast("At least one platform must be selected", {
        severity: "error"
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      const variables = {
        ...formData,
        startsAt: formData.startsAt
          ? new Date(formData.startsAt).toISOString()
          : null,
        endsAt: formData.endsAt
          ? new Date(formData.endsAt).toISOString()
          : null,
        title: formData.title || null,
        deeplink: formData.deeplink || null,
        externalUrl: formData.externalUrl || null,
        minAppVersion: formData.minAppVersion || null,
        maxAppVersion: formData.maxAppVersion || null,
        icon: formData.icon || null
      };

      if (isEdit) {
        await updateBanner({
          variables: { id: banner.id, ...variables }
        });
      } else {
        await createBanner({ variables });
      }

      showToast("Changes saved successfully.", { severity: "success" });
      onSuccess();
    } catch (err) {
      showToast(err.message, { severity: "error" });
    }
  };

  const loading = creating || updating;

  return {
    formData,
    loading,
    isEdit,
    handleChange,
    handlePlatformChange,
    handleSubmit
  };
};
